import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connectorRequest } from './connectorApi';

const running = result => ['queued', 'running', 'awaiting_contacts'].includes(result?.status) || result?.pending;
const merge = (before, after = []) => [...new Map([...before, ...after].map(item => [`${item.type}:${item.value}`, item])).values()];

// Same contact controls as Talent Search, using the connector account's wallet.
// Only a button click starts work. Reopens and retries poll the saved job instead.
export default function ConnectorContactLookup({ user, candidateId, searchId, shortlistId,
  contacts = [], lookup, isRTL = false }) {
  useUiLanguage();
  const tr = (en, ar) => isRTL ? ar : en;
  const [values, setValues] = useState(contacts);
  const [progress, setProgress] = useState(lookup);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const active = useRef(null);
  const job = useRef(lookup?.job_id);

  const run = useCallback(async (channel, resume = false) => {
    if (active.current) return;
    const controller = new AbortController();
    active.current = controller;
    setError('');
    setProgress(previous => ({ ...previous, status: 'running' }));
    const signal = controller.signal;
    const apply = result => {
      setValues(previous => merge(previous, result.contacts));
      setProgress(result);
    };
    try {
      if (!resume && !job.current) {
        const result = await connectorRequest(user, 'contact-unlock', { signal, body: {
          candidate_id: candidateId, ...(searchId ? { search_id: searchId } : { shortlist_id: shortlistId }), channel,
        } });
        if (signal.aborted) return;
        job.current = result.job_id;
        apply(result);
        if (!job.current) {
          // Previously unlocked contacts or a completed negative lookup are free.
          setProgress({ ...result, status: 'complete' });
          return;
        }
      }
      while (job.current && !signal.aborted) {
        const result = await connectorRequest(user, `results?job_id=${encodeURIComponent(job.current)}`, { signal });
        if (signal.aborted) return;
        apply(result);
        if (['failed', 'interrupted'].includes(result.status)) throw new Error('lookup_failed');
        if (!running(result)) break;
        // Abort clears the timer when leaving the profile. Never start a second
        // lookup because an email arrived before the phone (or vice versa).
        await new Promise(resolve => {
          const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
          const timer = setTimeout(finish, 5000);
          signal.addEventListener('abort', finish, { once: true });
          if (signal.aborted) finish();
        });
      }
    } catch (exception) {
      if (!signal.aborted) {
        setError(exception.code || 'lookup_failed');
        setProgress(previous => ({ ...previous, status: 'error', pending: false }));
      }
    } finally {
      if (active.current === controller) active.current = null;
    }
  }, [user, candidateId, searchId, shortlistId]);

  useEffect(() => {
    if (job.current && running(lookup)) run(null, true);
    return () => { active.current?.abort(); active.current = null; };
  }, [lookup, run]);

  async function copy(value) {
    try { await navigator.clipboard.writeText(value); setCopied(value); }
    catch { setCopied(''); } // Contact text remains selectable when clipboard access is blocked.
  }
  const busy = running(progress);
  const completed = progress?.status === 'complete' || progress?.status === 'not_found';
  const blocked = ['insufficient_credits', 'access_required'].includes(error);
  const field = (channel, icon) => {
    const found = values.filter(value => value.type === channel);
    // Personal email first, then work. Preserve all returned contact values.
    if (channel === 'email') found.sort((a, b) => Number(/personal/i.test(b.email_type)) - Number(/personal/i.test(a.email_type)));
    if (found.length) return found.map(contact => <button type="button" className="tp-contact-item connector-contact-button"
      key={`${channel}:${contact.value}`} onClick={() => copy(contact.value)} title={tr('Copy', 'نسخ')}>
      <i className={`fa-solid ${icon} tp-contact-icon`} aria-hidden="true" /><span dir="ltr">{contact.value}</span>
      <i className={`fa-solid ${copied === contact.value ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
    </button>);
    const missing = completed || progress?.contact_status?.[channel] === 'not_found';
    const pending = busy && !missing;
    const label = pending ? tr('Fetching...', 'جارٍ البحث…') : missing
      ? channel === 'email' ? tr('No Email', 'لا يوجد بريد إلكتروني') : tr('No Phone', 'لا يوجد رقم هاتف')
      : error ? tr('Retry lookup', 'إعادة المحاولة') : channel === 'email' ? tr('Get Email', 'عرض البريد الإلكتروني') : tr('Get Phone', 'عرض رقم الهاتف');
    return <button type="button" className={`tp-contact-item connector-contact-button${!missing && !pending ? ' tp-contact-unlock' : ''}`}
      disabled={busy || missing || blocked} aria-label={`${channel === 'email' ? uiText("Email") : uiText("Phone")}: ${label}`}
      aria-busy={pending || undefined} onClick={() => run(channel)}>
      <i className={`fa-solid ${icon} tp-contact-icon`} aria-hidden="true" /><span>{uiText(label)}</span>
      {!missing && <i className={`fa-solid ${pending ? 'fa-spinner fa-spin' : 'fa-unlock'}`} aria-hidden="true" />}
    </button>;
  };
  return <>
    {field('email', 'fa-envelope')}{field('phone', 'fa-phone')}
    {error && <div className="connector-contact-notice" role="alert">{blocked
        ? tr('Contact lookup needs account credit or activation. ', 'يتطلب عرض بيانات الاتصال رصيداً أو تفعيل الحساب. ')
        : tr('Contact lookup could not complete. Please retry; your existing request will be reused.', 'تعذر إكمال البحث. حاول مجدداً؛ سيتم استخدام طلبك الحالي.')}
        {blocked && <a href="/connectors/talent/access">{tr('View account access', 'عرض صلاحية الحساب')}</a>}
    </div>}
  </>;
}
